import { Controller } from "@hotwired/stimulus";
import consumer from "../channels/consumer";
import { Device } from "@twilio/voice-sdk";
import { TelnyxRTC } from "@telnyx/webrtc";

// Connects to data-controller="help-chat"
export default class extends Controller {
  static targets = [
    "bubble",
    "panel",
    "homeScreen",
    "chatScreen",
    "messages",
    "input",
    "sendButton",
    "callForm",
    "phoneInput",
    "nameInput",
    "preferredTimeInput",
    "typingIndicator",
    "liveAgentStatus",
    "liveAgentStatusText",
    // Voice call targets
    "voiceCallScreen",
    "voiceCallStatus",
    "voiceCallDuration",
    "voiceTranscript",
    "voiceMuteButton",
    "voiceSpeakerButton",
    "muteIconOn",
    "muteIconOff",
    "speakerIconOn",
    "speakerIconOff",
    "aiSpeakingIndicator"
  ];

  static values = {
    messagesUrl: String,
    callUrl: String,
    sessionUrl: String,
    pollUrl: String,
    resetUrl: String,
    voiceTokenUrl: String,
    locale: String,
    organizationId: String,
    phoneNumber: String
  };

  connect() {
    this.isOpen = false;
    this.isLoading = false;
    this.showingCallForm = false;
    this.inChatMode = false;

    // Chat subscription state
    this.chatSubscription = null;
    this.chatSessionToken = this.loadPersistedSessionToken();
    this.liveAgentMode = false;
    this.pollInterval = null;
    this.displayedMessageIds = new Set();

    // Voice call state
    this.device = null;
    this.activeCall = null;
    this.isMuted = false;
    this.callDurationInterval = null;
    this.callStartTime = null;
    this.transcriptSubscription = null;
    this.sessionToken = null;
    this.voiceProvider = null; // "twilio" or "telnyx"
    this.telnyxNumber = null;
    this.remoteAudio = null;

    // Bind keyboard events
    this.handleKeydown = this.handleKeydown.bind(this);
  }

  disconnect() {
    document.removeEventListener("keydown", this.handleKeydown);
    this.cleanupChatSubscription();
    this.stopPolling();
    this.cleanupVoiceCall();
  }

  sessionStorageKey() {
    return `help_chat_session_${this.organizationIdValue}`;
  }

  loadPersistedSessionToken() {
    try {
      return localStorage.getItem(this.sessionStorageKey());
    } catch {
      return null;
    }
  }

  persistSessionToken(token) {
    if (!token) return;

    this.chatSessionToken = token;

    try {
      localStorage.setItem(this.sessionStorageKey(), token);
    } catch {
      // localStorage may be unavailable in some embed contexts
    }
  }

  apiUrl(url) {
    const api = new URL(url, window.location.origin);
    if (this.chatSessionToken) {
      api.searchParams.set("session_token", this.chatSessionToken);
    }
    return api.toString();
  }

  apiJsonBody(payload = {}) {
    const body = { ...payload };
    if (this.chatSessionToken) {
      body.session_token = this.chatSessionToken;
    }
    return body;
  }

  // Clean up chat subscription
  cleanupChatSubscription() {
    if (this.chatSubscription) {
      this.chatSubscription.unsubscribe();
      this.chatSubscription = null;
    }
  }

  cleanupVoiceCall() {
    // End active call if any
    if (this.activeCall) {
      try {
        if (this.voiceProvider === "telnyx") {
          this.activeCall.hangup();
        } else {
          this.activeCall.disconnect();
        }
      } catch (e) {
        console.warn("Help Chat: Error ending call during cleanup:", e);
      }
      this.activeCall = null;
    }

    // Destroy/disconnect device
    if (this.device) {
      try {
        if (this.voiceProvider === "telnyx") {
          this.device.disconnect();
        } else {
          this.device.destroy();
        }
      } catch (e) {
        console.warn("Help Chat: Error destroying device during cleanup:", e);
      }
      this.device = null;
    }

    // Clean up Telnyx audio element
    if (this.remoteAudio) {
      this.remoteAudio.srcObject = null;
      this.remoteAudio = null;
    }

    // Clear duration timer
    if (this.callDurationInterval) {
      clearInterval(this.callDurationInterval);
      this.callDurationInterval = null;
    }

    // Unsubscribe from ActionCable
    if (this.transcriptSubscription) {
      this.transcriptSubscription.unsubscribe();
      this.transcriptSubscription = null;
    }

    // Reset provider state
    this.voiceProvider = null;
    this.telnyxNumber = null;
  }

  // Toggle chat panel visibility
  toggle(event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    this.isOpen = !this.isOpen;

    if (this.hasPanelTarget) {
      if (this.isOpen) {
        this.panelTarget.classList.remove("hidden");
        this.panelTarget.classList.add("animate-slide-up");
        document.addEventListener("keydown", this.handleKeydown);
      } else {
        this.panelTarget.classList.add("hidden");
        this.panelTarget.classList.remove("animate-slide-up");
        document.removeEventListener("keydown", this.handleKeydown);
      }
    }

    // Toggle bubble appearance
    if (this.hasBubbleTarget) {
      if (this.isOpen) {
        this.bubbleTarget.classList.add("chat-open");
      } else {
        this.bubbleTarget.classList.remove("chat-open");
      }
    }
  }

  // Close panel
  close(event) {
    if (event) {
      event.preventDefault();
    }
    if (this.isOpen) {
      this.toggle();
    }
  }

  // Handle keyboard events
  handleKeydown(event) {
    if (event.key === "Escape") {
      this.close();
    }
  }

  // Start chat conversation - switches from home to chat screen
  async startChat(event) {
    if (event) {
      event.preventDefault();
    }

    this.inChatMode = true;

    // Hide home screen, show chat screen
    if (this.hasHomeScreenTarget) {
      this.homeScreenTarget.classList.add("hidden");
    }

    if (this.hasChatScreenTarget) {
      this.chatScreenTarget.classList.remove("hidden");
    }

    // Load session and greeting if not already loaded
    if (!this.sessionLoaded) {
      try {
        await this.loadSession();
      } catch (error) {
        console.error("Help Chat: Error loading session:", error);
      }
    }

    // Focus input
    setTimeout(() => {
      if (this.hasInputTarget) {
        this.inputTarget.focus();
      }
    }, 100);
  }

  // Go back to home screen
  goHome(event) {
    if (event) {
      event.preventDefault();
    }

    this.inChatMode = false;

    // Hide chat screen, show home screen
    if (this.hasChatScreenTarget) {
      this.chatScreenTarget.classList.add("hidden");
    }
    if (this.hasHomeScreenTarget) {
      this.homeScreenTarget.classList.remove("hidden");
    }
  }

  // Start a new conversation (clear session and messages)
  async newConversation(event) {
    if (event) {
      event.preventDefault();
    }

    // Clear messages display immediately for better UX
    if (this.hasMessagesTarget) {
      this.messagesTarget.innerHTML = "";
    }
    this.displayedMessageIds.clear();
    this.stopLiveAgentMode();

    // Reset session via server endpoint
    if (this.hasResetUrlValue) {
      try {
        const response = await fetch(this.apiUrl(this.resetUrlValue), {
          method: "POST",
          headers: {
            "Accept": "application/json",
            "X-Requested-With": "XMLHttpRequest"
          },
          credentials: "same-origin",
          body: JSON.stringify(this.apiJsonBody())
        });

        if (response.ok) {
          const data = await response.json();

          if (data.session_token) {
            this.persistSessionToken(data.session_token);
            this.subscribeToChatChannel();
          }

          // Show the new greeting
          if (data.greeting) {
            this.addMessage("assistant", data.greeting);
          }

          // Mark session as loaded since we just got fresh data
          this.sessionLoaded = true;
        } else {
          console.error("Help Chat: Failed to reset session");
          // Fallback: try to reload session
          this.sessionLoaded = false;
          this.loadSession();
        }
      } catch (error) {
        console.error("Help Chat: Error resetting session:", error);
        // Fallback: try to reload session
        this.sessionLoaded = false;
        this.loadSession();
      }
    } else {
      // Fallback if no reset URL
      this.sessionLoaded = false;
      this.loadSession();
    }
  }

  // Initiate voice call - for click tracking/analytics
  initiateVoiceCall(_event) {
    // The tel: link handles the actual call
    // This is for optional tracking or UI feedback
    // You could add analytics tracking here
  }

  // Load session and show greeting
  async loadSession() {
    if (!this.hasSessionUrlValue || this.sessionLoaded) return;

    try {
      const response = await fetch(this.apiUrl(this.sessionUrlValue), {
        method: "GET",
        headers: {
          "Accept": "application/json",
          "X-Requested-With": "XMLHttpRequest"
        },
        credentials: "same-origin"
      });

      if (response.ok) {
        const data = await response.json();
        this.sessionLoaded = true;

        // Store session token and subscribe to chat channel for real-time updates
        if (data.session_token) {
          this.persistSessionToken(data.session_token);
          this.subscribeToChatChannel();
        }

        this.applyLiveAgentState(data);

        // Display existing messages if any
        if (data.messages && data.messages.length > 0) {
          data.messages.forEach(msg => {
            this.addStoredMessage(msg);
          });
        } else if (data.greeting) {
          // Show greeting for new sessions
          this.addMessage("assistant", data.greeting);
        }
      }
    } catch (error) {
      console.error("Help Chat: Error loading session:", error);
    }
  }

  // Subscribe to HelpChatChannel for real-time updates (handoff notifications, agent messages)
  subscribeToChatChannel() {
    if (!this.chatSessionToken) {
      console.warn("Help Chat: No session token for chat channel subscription");
      return;
    }

    // Clean up any existing subscription
    this.cleanupChatSubscription();

    this.chatSubscription = consumer.subscriptions.create(
      { channel: "HelpChatChannel", session_token: this.chatSessionToken },
      {
        connected: () => {
          // Successfully connected to chat channel
        },
        disconnected: () => {
          // Disconnected from chat channel
        },
        rejected: () => {
          console.error("Help Chat: Chat channel subscription rejected");
          if (this.liveAgentMode) {
            this.startPolling();
          }
        },
        received: (data) => {
          this.handleChatChannelMessage(data);
        }
      }
    );
  }

  // Handle incoming messages from HelpChatChannel
  handleChatChannelMessage(data) {
    switch (data.type) {
    case "handoff":
      this.startLiveAgentMode({ agentName: data.agent_name, connected: true });
      if (data.message) {
        this.addMessage("system", data.message);
      }
      break;
    case "agent_message":
      if (data.message && data.message.body) {
        this.addMessage("agent", data.message.body, {
          messageId: data.message.id,
          senderName: data.message.sender_name
        });
      }
      break;
    case "agent_typing":
      if (data.is_typing) {
        this.showTypingIndicator(this.getLocalizedMessage("agentTyping"));
      } else {
        this.hideTypingIndicator();
      }
      break;
    case "status_changed":
      if (data.status === "waiting_for_agent") {
        this.startLiveAgentMode({ waiting: true });
        if (data.message) {
          this.addMessage("system", data.message);
        }
      } else if (data.status === "resolved") {
        this.stopLiveAgentMode();
        this.addMessage("system", data.message || this.getLocalizedMessage("conversationResolved"));
      }
      break;
    }
  }

  applyLiveAgentState(data) {
    if (data.human_agent_mode) {
      this.startLiveAgentMode({ agentName: data.agent_name, connected: true });
    } else if (data.waiting_for_agent) {
      this.startLiveAgentMode({ waiting: true });
    }
  }

  startLiveAgentMode({ agentName = null, connected = false, waiting = false } = {}) {
    this.liveAgentMode = true;
    this.startPolling();

    if (connected) {
      this.showLiveAgentStatus(
        agentName
          ? this.getLocalizedMessage("agentConnected", { name: agentName })
          : this.getLocalizedMessage("agentConnectedGeneric")
      );
    } else if (waiting) {
      this.showLiveAgentStatus(this.getLocalizedMessage("waitingForAgent"));
    }
  }

  stopLiveAgentMode() {
    this.liveAgentMode = false;
    this.stopPolling();
    this.hideLiveAgentStatus();
  }

  showLiveAgentStatus(message) {
    if (!this.hasLiveAgentStatusTarget || !this.hasLiveAgentStatusTextTarget) return;

    this.liveAgentStatusTextTarget.textContent = message;
    this.liveAgentStatusTarget.classList.remove("hidden");
  }

  hideLiveAgentStatus() {
    if (!this.hasLiveAgentStatusTarget) return;

    this.liveAgentStatusTarget.classList.add("hidden");
  }

  startPolling() {
    if (!this.hasPollUrlValue || this.pollInterval) return;

    this.pollInterval = setInterval(() => {
      this.pollForMessages();
    }, 3000);
  }

  stopPolling() {
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
      this.pollInterval = null;
    }
  }

  async pollForMessages() {
    if (!this.hasPollUrlValue || !this.chatSessionToken) return;

    try {
      const response = await fetch(this.apiUrl(this.pollUrlValue), {
        method: "GET",
        headers: {
          "Accept": "application/json",
          "X-Requested-With": "XMLHttpRequest"
        },
        credentials: "same-origin"
      });

      if (!response.ok) return;

      const data = await response.json();
      this.applyLiveAgentState(data);

      if (data.messages && data.messages.length > 0) {
        data.messages.forEach(msg => {
          this.addStoredMessage(msg);
        });
      }
    } catch (error) {
      console.error("Help Chat: Error polling messages:", error);
    }
  }

  addStoredMessage(msg) {
    const role = this.messageRoleFromStored(msg);
    const senderName = msg.metadata?.agent_name;
    // Three identities, in the order the bubble was drawn under them: the key the
    // visitor's own browser chose, the conversation message id the channel used
    // for an agent reply, and finally the session's own id.
    const messageId = msg.metadata?.client_message_id || msg.metadata?.message_id || msg.id;
    this.addMessage(role, msg.content, { messageId, senderName });
  }

  // The bubbles this response accounts for have already been drawn here, so claim
  // their identities before the poll offers them again.
  claimServerMessages(data) {
    const ids = data?.message_ids;
    if (!Array.isArray(ids)) return;

    ids.forEach((id) => {
      if (id) this.displayedMessageIds.add(id);
    });
  }

  // An identity for a bubble drawn before the server has seen the message. Stable
  // across the round trip, which is what lets the poll recognise it.
  newClientMessageId() {
    if (window.crypto && typeof window.crypto.randomUUID === "function") {
      return window.crypto.randomUUID();
    }

    return `bubble-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  }

  messageRoleFromStored(msg) {
    if (msg.role === "user") return "user";
    if (msg.metadata?.source === "live_agent") return "agent";
    if (msg.role === "system") return "system";
    return "assistant";
  }

  handleLiveAgentResponse(data) {
    if (data.action === "escalate" || data.action === "human_agent_mode") {
      if (data.human_agent_mode) {
        this.startLiveAgentMode({ agentName: data.agent_name, connected: true });
      } else if (data.waiting_for_agent) {
        this.startLiveAgentMode({ waiting: true });
      }
    }
  }

  // Send message
  async send(event) {
    if (event) {
      event.preventDefault();
    }

    if (!this.hasInputTarget || this.isLoading) return;

    const content = this.inputTarget.value.trim();
    if (!content) return;

    // Clear input and disable while sending
    this.inputTarget.value = "";
    this.setLoading(true);

    // Draw the visitor's own message straight away, under a key we choose, so the
    // same bubble coming back from the poll is recognised rather than redrawn.
    const clientMessageId = this.newClientMessageId();
    this.addMessage("user", content, { messageId: clientMessageId });

    try {
      const response = await fetch(this.apiUrl(this.messagesUrlValue), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-Requested-With": "XMLHttpRequest"
        },
        credentials: "same-origin",
        body: JSON.stringify(this.apiJsonBody({ content, client_message_id: clientMessageId }))
      });

      if (response.ok) {
        const data = await response.json();

        if (data.session_token) {
          this.persistSessionToken(data.session_token);
        }

        this.claimServerMessages(data);

        // Add AI response (skip if in human agent mode - response will be null)
        if (data.response) {
          this.addMessage("assistant", data.response);
        }

        this.handleLiveAgentResponse(data);

        // Handle special actions
        if (data.action === "show_call_form") {
          this.showCallForm();
        }
      } else {
        const errorData = await response.json();
        this.addMessage("system", errorData.error || "Sorry, something went wrong.");
      }
    } catch (error) {
      console.error("Help Chat: Error sending message:", error);
      this.addMessage("system", "Unable to send message. Please try again.");
    } finally {
      this.setLoading(false);
    }
  }

  // Add message to the chat
  addMessage(role, content, { messageId = null, senderName = null } = {}) {
    if (!this.hasMessagesTarget) return;

    if (messageId) {
      if (this.displayedMessageIds.has(messageId)) return;
      this.displayedMessageIds.add(messageId);
    }

    const messageDiv = document.createElement("div");
    messageDiv.className = `flex ${role === "user" ? "justify-end" : "justify-start"} mb-3`;

    const bubbleClass = role === "user"
      ? "bg-blue-600 text-white rounded-2xl rounded-br-md"
      : role === "system"
        ? "bg-amber-100 text-amber-900 dark:bg-amber-900/30 dark:text-amber-100 rounded-2xl"
        : role === "agent"
          ? "bg-emerald-100 text-emerald-900 dark:bg-emerald-900/30 dark:text-emerald-100 rounded-2xl rounded-bl-md"
          : "bg-gray-100 text-gray-900 dark:bg-gray-700 dark:text-gray-100 rounded-2xl rounded-bl-md";

    const label = role === "agent"
      ? (senderName || this.getLocalizedMessage("agentLabel"))
      : null;

    const labelHtml = label
      ? `<span class="text-xs font-medium opacity-80 block mb-1">${this.escapeHtml(label)}</span>`
      : "";

    messageDiv.innerHTML = `
      <div class="max-w-[85%] px-4 py-2 ${bubbleClass} shadow-sm">
        ${labelHtml}
        <p class="text-sm whitespace-pre-wrap break-words">${this.linkifyText(this.escapeHtml(content))}</p>
      </div>
    `;

    this.messagesTarget.appendChild(messageDiv);

    this.scrollToBottom();
  }

  // Show typing indicator
  showTypingIndicator(label = null) {
    if (this.hasTypingIndicatorTarget) {
      const textEl = this.typingIndicatorTarget.querySelector("span.text-xs");
      if (textEl && label) {
        textEl.textContent = label;
      }
      this.typingIndicatorTarget.classList.remove("hidden");
      this.scrollToBottom();
    }
  }

  // Hide typing indicator
  hideTypingIndicator() {
    if (this.hasTypingIndicatorTarget) {
      this.typingIndicatorTarget.classList.add("hidden");
    }
  }

  // Set loading state
  setLoading(loading) {
    this.isLoading = loading;

    if (this.hasSendButtonTarget) {
      this.sendButtonTarget.disabled = loading;
    }

    if (this.hasInputTarget) {
      this.inputTarget.disabled = loading;
    }

    if (loading) {
      this.showTypingIndicator();
    } else {
      this.hideTypingIndicator();
      if (this.hasInputTarget) {
        this.inputTarget.focus();
      }
    }
  }

  // Show call request form
  showCallForm() {
    if (!this.hasCallFormTarget) return;

    this.showingCallForm = true;
    this.callFormTarget.classList.remove("hidden");

    // Focus phone input
    if (this.hasPhoneInputTarget) {
      setTimeout(() => this.phoneInputTarget.focus(), 100);
    }

    this.scrollToBottom();
  }

  // Hide call request form
  hideCallForm() {
    if (!this.hasCallFormTarget) return;

    this.showingCallForm = false;
    this.callFormTarget.classList.add("hidden");
  }

  // Cancel call request
  cancelCall(event) {
    if (event) event.preventDefault();
    this.hideCallForm();
    this.addMessage("assistant", this.getLocalizedMessage("callCancelled"));
  }

  // Submit call request
  async submitCallRequest(event) {
    if (event) event.preventDefault();

    if (!this.hasPhoneInputTarget) return;

    const phoneNumber = this.phoneInputTarget.value.trim();
    const name = this.hasNameInputTarget ? this.nameInputTarget.value.trim() : "";
    const preferredTime = this.hasPreferredTimeInputTarget ? this.preferredTimeInputTarget.value.trim() : "";

    if (!phoneNumber) {
      this.phoneInputTarget.focus();
      return;
    }

    this.setLoading(true);

    try {
      const response = await fetch(this.apiUrl(this.callUrlValue), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-Requested-With": "XMLHttpRequest"
        },
        credentials: "same-origin",
        body: JSON.stringify(this.apiJsonBody({
          phone_number: phoneNumber,
          name: name,
          preferred_time: preferredTime
        }))
      });

      if (response.ok) {
        const data = await response.json();
        this.hideCallForm();
        this.claimServerMessages(data);
        this.addMessage("assistant", data.message);

        // Clear form
        if (this.hasPhoneInputTarget) this.phoneInputTarget.value = "";
        if (this.hasNameInputTarget) this.nameInputTarget.value = "";
        if (this.hasPreferredTimeInputTarget) this.preferredTimeInputTarget.value = "";
      } else {
        const errorData = await response.json();
        this.addMessage("system", errorData.error || "Unable to process callback request.");
      }
    } catch (error) {
      console.error("Help Chat: Error submitting call request:", error);
      this.addMessage("system", "Unable to submit request. Please try again.");
    } finally {
      this.setLoading(false);
    }
  }

  // Handle Enter key in input
  handleInputKeydown(event) {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      this.send();
    }
  }

  // Scroll messages to bottom
  scrollToBottom() {
    if (this.hasMessagesTarget) {
      this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight;
    }
  }

  // Escape HTML to prevent XSS
  escapeHtml(text) {
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML;
  }

  // Linkify text - makes phone numbers, emails, and URLs clickable
  // Must be called AFTER escapeHtml to avoid XSS
  linkifyText(escapedText) {
    if (!escapedText) return escapedText;

    // Phone pattern - matches international formats
    const phonePattern = /(?:\+\d{1,4}[\s.-]?)?(?:\(?\d{1,4}\)?[\s.-]?)(?:\d{1,4}[\s.-]?){1,3}\d{1,4}/g;

    // Email pattern
    const emailPattern = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g;

    // URL pattern - matches http, https, and www URLs
    const urlPattern = /(?:https?:\/\/|www\.)[^\s<>[\]]+(?<![.,;:!?\]}])/gi;

    const linkClass = "text-indigo-600 dark:text-indigo-400 hover:underline font-medium";

    // Replace URLs first (before email, as URLs might contain @)
    let result = escapedText.replace(urlPattern, (url) => {
      const href = url.startsWith("http") ? url : `https://${url}`;
      return `<a href="${href}" target="_blank" rel="noopener noreferrer" class="${linkClass}">${url}</a>`;
    });

    // Replace emails
    result = result.replace(emailPattern, (email) => {
      return `<a href="mailto:${email}" class="${linkClass}">${email}</a>`;
    });

    // Replace phone numbers (at least 7 digits to avoid false positives)
    result = result.replace(phonePattern, (phone) => {
      const digitsOnly = phone.replace(/\D/g, "");
      if (digitsOnly.length < 7) return phone;

      const telHref = phone.includes("+") ? phone.replace(/[^\d+]/g, "") : digitsOnly;
      return `<a href="tel:${telHref}" class="${linkClass}">${phone}</a>`;
    });

    return result;
  }

  // Get localized messages
  getLocalizedMessage(key, vars = {}) {
    const messages = {
      en: {
        callCancelled: "No problem! Let me know if you need anything else.",
        connecting: "Connecting...",
        connected: "Connected",
        callEnded: "Call ended",
        callFailed: "Call failed. Please try again.",
        waitingForAgent: "Connecting you to a human agent…",
        agentConnected: "{name} has joined the chat",
        agentConnectedGeneric: "A human agent has joined the chat",
        agentLabel: "Agent",
        agentTyping: "Agent is typing...",
        conversationResolved: "This conversation has been resolved. You can start a new chat anytime."
      },
      es: {
        callCancelled: "Perfecto! Hazme saber si necesitas algo más.",
        connecting: "Conectando...",
        connected: "Conectado",
        callEnded: "Llamada terminada",
        callFailed: "La llamada falló. Por favor intente de nuevo.",
        waitingForAgent: "Conectándote con un agente humano…",
        agentConnected: "{name} se ha unido al chat",
        agentConnectedGeneric: "Un agente humano se ha unido al chat",
        agentLabel: "Agente",
        agentTyping: "El agente está escribiendo...",
        conversationResolved: "Esta conversación ha sido resuelta. Puedes iniciar un nuevo chat en cualquier momento."
      },
      de: {
        callCancelled: "Kein Problem! Lassen Sie mich wissen, wenn Sie etwas anderes brauchen.",
        connecting: "Verbindung wird hergestellt...",
        connected: "Verbunden",
        callEnded: "Anruf beendet",
        callFailed: "Anruf fehlgeschlagen. Bitte versuchen Sie es erneut.",
        waitingForAgent: "Sie werden mit einem Mitarbeiter verbunden…",
        agentConnected: "{name} ist dem Chat beigetreten",
        agentConnectedGeneric: "Ein Mitarbeiter ist dem Chat beigetreten",
        agentLabel: "Mitarbeiter",
        agentTyping: "Mitarbeiter schreibt...",
        conversationResolved: "Diese Unterhaltung wurde abgeschlossen. Sie können jederzeit einen neuen Chat starten."
      },
      fr: {
        callCancelled: "Pas de problème! Faites-moi savoir si vous avez besoin d'autre chose.",
        connecting: "Connexion en cours...",
        connected: "Connecté",
        callEnded: "Appel terminé",
        callFailed: "L'appel a échoué. Veuillez réessayer.",
        waitingForAgent: "Connexion à un agent humain…",
        agentConnected: "{name} a rejoint le chat",
        agentConnectedGeneric: "Un agent humain a rejoint le chat",
        agentLabel: "Agent",
        agentTyping: "L'agent écrit...",
        conversationResolved: "Cette conversation a été résolue. Vous pouvez démarrer un nouveau chat à tout moment."
      },
      it: {
        callCancelled: "Nessun problema! Fammi sapere se hai bisogno di altro.",
        connecting: "Connessione in corso...",
        connected: "Connesso",
        callEnded: "Chiamata terminata",
        callFailed: "Chiamata fallita. Per favore riprova."
      },
      pt: {
        callCancelled: "Sem problemas! Me avise se precisar de mais alguma coisa.",
        connecting: "Conectando...",
        connected: "Conectado",
        callEnded: "Chamada encerrada",
        callFailed: "A chamada falhou. Por favor, tente novamente."
      },
      nl: {
        callCancelled: "Geen probleem! Laat me weten als u nog iets nodig heeft.",
        connecting: "Verbinden...",
        connected: "Verbonden",
        callEnded: "Gesprek beëindigd",
        callFailed: "Oproep mislukt. Probeer het opnieuw."
      },
      el: {
        callCancelled: "Κανένα πρόβλημα! Πείτε μου αν χρειάζεστε κάτι άλλο.",
        connecting: "Σύνδεση...",
        connected: "Συνδεδεμένο",
        callEnded: "Η κλήση τερματίστηκε",
        callFailed: "Η κλήση απέτυχε. Παρακαλώ δοκιμάστε ξανά."
      }
    };

    const locale = this.localeValue || "en";
    const localeMessages = messages[locale] || messages.en;
    let message = localeMessages[key] || messages.en[key] || "";

    Object.entries(vars).forEach(([name, value]) => {
      message = message.replace(`{${name}}`, value);
    });

    return message;
  }

  // ==========================================
  // Voice Call Methods
  // ==========================================

  // Start a browser-based voice call with the AI agent
  async startVoiceCall(event) {
    if (event) event.preventDefault();

    // Show voice call screen
    this.showVoiceCallScreen();

    try {
      // Get voice token from server
      const tokenResponse = await fetch(this.voiceTokenUrlValue, {
        method: "GET",
        headers: {
          "Accept": "application/json",
          "X-Requested-With": "XMLHttpRequest"
        },
        credentials: "same-origin"
      });

      if (!tokenResponse.ok) {
        const errorData = await tokenResponse.json();
        throw new Error(errorData.error || "Failed to get voice token");
      }

      const tokenData = await tokenResponse.json();

      // Store session token for ActionCable subscription
      this.sessionToken = tokenData.session_token;
      this.voiceProvider = tokenData.provider || "twilio";
      this.telnyxNumber = tokenData.telnyx_number;

      console.warn(`Help Chat: Using ${this.voiceProvider} for voice call`);

      if (this.voiceProvider === "telnyx") {
        await this.initializeTelnyxCall(tokenData.token);
      } else {
        await this.initializeTwilioCall(tokenData.token);
      }

      // Subscribe to transcripts via ActionCable
      this.subscribeToTranscripts();

    } catch (error) {
      console.error("Help Chat: Voice call error:", error);
      this.updateVoiceCallStatus(this.getLocalizedMessage("callFailed"));

      // Show error for a moment then go back
      setTimeout(() => {
        this.exitVoiceCall();
      }, 3000);
    }
  }

  // Initialize Twilio voice call
  async initializeTwilioCall(token) {
    // Initialize Twilio Device (using @twilio/voice-sdk v2.x)
    this.device = new Device(token, {
      logLevel: 1, // 0=off, 1=error, 2=warn, 3=info, 4=debug
      codecPreferences: ["opus", "pcmu"]
    });

    // Set up device event handlers
    this.setupTwilioDeviceEventHandlers();

    // Register the device
    await this.device.register();

    // Connect the call - no parameters needed, identity is in the token
    this.activeCall = await this.device.connect();

    // Set up call event handlers
    this.setupTwilioCallEventHandlers();
  }

  // Initialize Telnyx voice call
  async initializeTelnyxCall(token) {
    // Create audio element for remote stream
    this.remoteAudio = document.getElementById("help-chat-remote-audio");
    if (!this.remoteAudio) {
      this.remoteAudio = document.createElement("audio");
      this.remoteAudio.id = "help-chat-remote-audio";
      this.remoteAudio.autoplay = true;
      this.remoteAudio.setAttribute("playsinline", "");
      document.body.appendChild(this.remoteAudio);
    }

    // Initialize TelnyxRTC device
    this.device = new TelnyxRTC({
      login_token: token
    });

    // Connect the remote stream to the audio element
    this.device.remoteElement = this.remoteAudio;

    // Set up Telnyx device event handlers
    this.setupTelnyxDeviceEventHandlers();

    // Connect to Telnyx
    this.device.connect();

    // Wait for device to be ready before making call
    await new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error("Telnyx device connection timeout"));
      }, 10000);

      this.device.on("telnyx.ready", () => {
        clearTimeout(timeout);
        console.warn("Help Chat: Telnyx device ready");
        resolve();
      });

      this.device.on("telnyx.error", (error) => {
        clearTimeout(timeout);
        reject(error);
      });
    });

    // Make the outbound call to the Telnyx number
    // The session token is passed via clientState so the webhook can identify the session
    if (!this.telnyxNumber) {
      throw new Error("No Telnyx number configured for help chat voice calls");
    }

    this.activeCall = this.device.newCall({
      destinationNumber: this.telnyxNumber,
      clientState: btoa(JSON.stringify({
        type: "help_chat_voice",
        session_token: this.sessionToken
      })),
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true
      },
      video: false
    });

    console.warn("Help Chat: Telnyx call initiated to", this.telnyxNumber);
  }

  // Set up Telnyx Device event handlers
  setupTelnyxDeviceEventHandlers() {
    this.device.on("telnyx.error", (error) => {
      console.error("Help Chat: Telnyx device error:", error);
      this.updateVoiceCallStatus(this.getLocalizedMessage("callFailed"));
    });

    this.device.on("telnyx.notification", (notification) => {
      if (notification.type === "callUpdate" && this.activeCall) {
        const call = notification.call;
        if (call.id === this.activeCall.id) {
          if (call.state === "active") {
            console.warn("Help Chat: Telnyx call active");
            this.updateVoiceCallStatus(this.getLocalizedMessage("connected"));
            this.startCallDurationTimer();
            this.resumeTelnyxAudioContext();
          } else if (call.state === "destroy") {
            console.warn("Help Chat: Telnyx call ended");
            this.updateVoiceCallStatus(this.getLocalizedMessage("callEnded"));
            this.stopCallDurationTimer();
            setTimeout(() => this.exitVoiceCall(), 2000);
          } else if (call.state === "ringing") {
            this.updateVoiceCallStatus(this.getLocalizedMessage("connecting"));
          }
        }
      }
    });
  }

  // Resume Telnyx audio context (browser autoplay policy)
  resumeTelnyxAudioContext() {
    try {
      if (this.remoteAudio && this.remoteAudio.paused) {
        this.remoteAudio.play().catch(e => console.warn("Could not auto-play remote audio:", e));
      }
    } catch (error) {
      console.warn("Unable to resume Telnyx audio context:", error);
    }
  }

  // Set up Twilio Device event handlers
  setupTwilioDeviceEventHandlers() {
    this.device.on("registered", () => {});

    this.device.on("error", (error) => {
      console.error("Help Chat: Twilio device error:", error);
      this.updateVoiceCallStatus(this.getLocalizedMessage("callFailed"));
    });

    this.device.on("unregistered", () => {});
  }

  // Set up Twilio call event handlers
  setupTwilioCallEventHandlers() {
    if (!this.activeCall) return;

    this.activeCall.on("accept", () => {
      this.updateVoiceCallStatus(this.getLocalizedMessage("connected"));
      this.startCallDurationTimer();
    });

    this.activeCall.on("disconnect", () => {
      this.updateVoiceCallStatus(this.getLocalizedMessage("callEnded"));
      this.stopCallDurationTimer();

      // Show end message briefly then return to home
      setTimeout(() => {
        this.exitVoiceCall();
      }, 2000);
    });

    this.activeCall.on("cancel", () => {
      this.updateVoiceCallStatus(this.getLocalizedMessage("callEnded"));
      this.stopCallDurationTimer();
    });

    this.activeCall.on("error", (error) => {
      console.error("Help Chat: Call error:", error);
      this.updateVoiceCallStatus(this.getLocalizedMessage("callFailed"));
      this.stopCallDurationTimer();
    });

    this.activeCall.on("ringing", () => {
      this.updateVoiceCallStatus(this.getLocalizedMessage("connecting"));
    });
  }

  // End the current voice call
  endVoiceCall(event) {
    if (event) event.preventDefault();

    if (this.activeCall) {
      if (this.voiceProvider === "telnyx") {
        // Telnyx uses hangup()
        try {
          this.activeCall.hangup();
        } catch (e) {
          console.error("Help Chat: Error hanging up Telnyx call:", e);
        }
      } else {
        // Twilio uses disconnect()
        this.activeCall.disconnect();
      }
    }

    this.updateVoiceCallStatus(this.getLocalizedMessage("callEnded"));
    this.stopCallDurationTimer();

    // Return to home after a brief moment
    setTimeout(() => {
      this.exitVoiceCall();
    }, 1500);
  }

  // Exit voice call screen and return to home
  exitVoiceCall(event) {
    if (event) event.preventDefault();

    // Clean up voice call resources
    this.cleanupVoiceCall();

    // Reset UI state
    this.isMuted = false;
    this.updateMuteButtonUI();

    // Clear transcripts
    if (this.hasVoiceTranscriptTarget) {
      this.voiceTranscriptTarget.innerHTML = "";
    }

    // Hide voice call screen, show home screen
    if (this.hasVoiceCallScreenTarget) {
      this.voiceCallScreenTarget.classList.add("hidden");
    }
    if (this.hasHomeScreenTarget) {
      this.homeScreenTarget.classList.remove("hidden");
    }

    // Reset duration display
    if (this.hasVoiceCallDurationTarget) {
      this.voiceCallDurationTarget.textContent = "00:00";
    }
  }

  // Show voice call screen
  showVoiceCallScreen() {
    // Hide other screens
    if (this.hasHomeScreenTarget) {
      this.homeScreenTarget.classList.add("hidden");
    }
    if (this.hasChatScreenTarget) {
      this.chatScreenTarget.classList.add("hidden");
    }

    // Show voice call screen
    if (this.hasVoiceCallScreenTarget) {
      this.voiceCallScreenTarget.classList.remove("hidden");
    }

    // Clear any previous transcripts from prior calls
    if (this.hasVoiceTranscriptTarget) {
      this.voiceTranscriptTarget.innerHTML = "";
    }

    // Reset duration display
    if (this.hasVoiceCallDurationTarget) {
      this.voiceCallDurationTarget.textContent = "00:00";
    }

    // Reset status
    this.updateVoiceCallStatus(this.getLocalizedMessage("connecting"));
  }

  // Update voice call status text
  updateVoiceCallStatus(status) {
    if (this.hasVoiceCallStatusTarget) {
      this.voiceCallStatusTarget.textContent = status;
    }
  }

  // Start call duration timer
  startCallDurationTimer() {
    this.callStartTime = Date.now();
    this.callDurationInterval = setInterval(() => {
      this.updateCallDuration();
    }, 1000);
  }

  // Stop call duration timer
  stopCallDurationTimer() {
    if (this.callDurationInterval) {
      clearInterval(this.callDurationInterval);
      this.callDurationInterval = null;
    }
  }

  // Update call duration display
  updateCallDuration() {
    if (!this.callStartTime || !this.hasVoiceCallDurationTarget) return;

    const elapsed = Math.floor((Date.now() - this.callStartTime) / 1000);
    const minutes = Math.floor(elapsed / 60).toString().padStart(2, "0");
    const seconds = (elapsed % 60).toString().padStart(2, "0");
    this.voiceCallDurationTarget.textContent = `${minutes}:${seconds}`;
  }

  // Toggle mute
  toggleMute(event) {
    if (event) event.preventDefault();

    if (!this.activeCall) return;

    this.isMuted = !this.isMuted;

    try {
      if (this.voiceProvider === "telnyx") {
        // Telnyx uses muteAudio()/unmuteAudio()
        if (this.isMuted) {
          this.activeCall.muteAudio();
        } else {
          this.activeCall.unmuteAudio();
        }
      } else {
        // Twilio uses mute(boolean)
        this.activeCall.mute(this.isMuted);
      }
    } catch (e) {
      console.error("Help Chat: Error toggling mute:", e);
    }

    this.updateMuteButtonUI();
  }

  // Update mute button UI
  updateMuteButtonUI() {
    if (this.hasMuteIconOnTarget && this.hasMuteIconOffTarget) {
      if (this.isMuted) {
        this.muteIconOnTarget.classList.remove("hidden");
        this.muteIconOffTarget.classList.add("hidden");
      } else {
        this.muteIconOnTarget.classList.add("hidden");
        this.muteIconOffTarget.classList.remove("hidden");
      }
    }
  }

  // Toggle speaker (placeholder - actual implementation depends on browser capabilities)
  toggleSpeaker(event) {
    if (event) event.preventDefault();
    // Note: Browser audio output selection is limited and varies by browser
  }

  // Subscribe to transcripts via ActionCable
  subscribeToTranscripts() {
    if (!this.sessionToken) {
      console.error("Help Chat: No session token for ActionCable subscription");
      return;
    }

    // Clean up any existing subscription first
    if (this.transcriptSubscription) {
      this.transcriptSubscription.unsubscribe();
      this.transcriptSubscription = null;
    }

    const subscriptionParams = { channel: "HelpChatCallChannel", session_token: this.sessionToken };

    // Use the pre-configured consumer from channels/consumer.js
    // which handles the WebSocket URL correctly for development/production
    this.transcriptSubscription = consumer.subscriptions.create(
      subscriptionParams,
      {
        initialized: () => {},
        connected: () => {},
        disconnected: () => {},
        rejected: () => {
          console.error("Help Chat: Subscription was REJECTED by server");
        },
        received: (data) => {
          this.handleTranscriptData(data);
        }
      }
    );
  }

  // Handle incoming transcript data
  handleTranscriptData(data) {
    switch (data.type) {
    case "transcript":
      this.addTranscriptBubble(data);
      break;
    case "call_status":
      this.updateVoiceCallStatus(data.status);
      break;
    case "action":
      this.handleVoiceAction(data);
      break;
    }
  }

  // Add a transcript bubble to the voice call screen
  addTranscriptBubble(data) {
    if (!this.hasVoiceTranscriptTarget) {
      return;
    }

    const speaker = data.speaker || "ai";
    const text = data.text || "";
    const isFinal = data.is_final !== false;

    // Create or update bubble
    const bubbleId = `transcript-${data.sequence || Date.now()}`;
    const existingBubble = this.voiceTranscriptTarget.querySelector(`#${bubbleId}`);

    if (existingBubble && !isFinal) {
      // Update existing partial transcript
      existingBubble.querySelector("p").textContent = text;
    } else {
      // Create new bubble
      const bubbleDiv = document.createElement("div");
      bubbleDiv.id = bubbleId;
      bubbleDiv.className = `flex ${speaker === "visitor" ? "justify-end" : "justify-start"} mb-3`;

      const bubbleClass = speaker === "visitor"
        ? "bg-blue-600 text-white rounded-2xl rounded-br-md"
        : "bg-gray-100 text-gray-900 dark:bg-gray-700 dark:text-gray-100 rounded-2xl rounded-bl-md";

      const labelText = speaker === "visitor" ? "You" : "AI";

      bubbleDiv.innerHTML = `
        <div class="max-w-[85%] px-4 py-2 ${bubbleClass} shadow-sm">
          <span class="text-xs opacity-70 block mb-1">${labelText}</span>
          <p class="text-sm whitespace-pre-wrap break-words">${this.linkifyText(this.escapeHtml(text))}</p>
        </div>
      `;

      this.voiceTranscriptTarget.appendChild(bubbleDiv);
      this.scrollVoiceTranscriptToBottom();
    }
  }

  // Handle voice call action events
  handleVoiceAction(data) {
    switch (data.action) {
    case "ticket_created":
      this.addTranscriptBubble({
        speaker: "ai",
        text: `A support ticket has been created (ID: ${data.ticket_id}).`,
        is_final: true,
        sequence: `action-${Date.now()}`
      });
      break;
    case "transfer_requested":
      this.addTranscriptBubble({
        speaker: "ai",
        text: "Transferring you to a human agent...",
        is_final: true,
        sequence: `action-${Date.now()}`
      });
      break;
    }
  }

  // Scroll voice transcript to bottom
  scrollVoiceTranscriptToBottom() {
    if (this.hasVoiceTranscriptTarget) {
      this.voiceTranscriptTarget.scrollTop = this.voiceTranscriptTarget.scrollHeight;
    }
  }
}
